/**
 * PractitionersPage - Admin page to view and manage all practitioners
 * Uses the shared TableView component for consistent UI with RecordsView
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye, Pencil } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  TableView,
  AvatarCell,
  StatusBadge,
  TextCell,
  type TableColumn,
} from '@/components/views/TableView';
import { PRACTITIONERS_TABLE_GRID } from '@/components/shared/AppointmentsTable';
import { apiClient } from '@/lib/api/client';
import { useTableFilters } from '@/lib/hooks';
import { PRACTITIONER_STATUS_OPTIONS } from '@/components/shared/DataTable';

interface Practitioner {
  id: string;
  resourceType: 'Practitioner';
  name?: Array<{
    given?: string[];
    family?: string;
    text?: string;
  }>;
  identifier?: Array<{
    system?: string;
    value?: string;
  }>;
  telecom?: Array<{
    system?: string;
    value?: string;
  }>;
  qualification?: Array<{
    code?: {
      text?: string;
    };
  }>;
}

interface PractitionerBundle {
  resourceType: 'Bundle';
  entry?: Array<{
    resource: Practitioner;
  }>;
  total?: number;
}

const PAGE_SIZE = 10;

// Helper functions
const getPractitionerName = (practitioner: Practitioner): string => {
  if (practitioner.name?.[0]) {
    const name = practitioner.name[0];
    if (name.text) return name.text;
    const given = name.given?.join(' ') || '';
    const family = name.family || '';
    return `${given} ${family}`.trim() || 'Unknown';
  }
  return 'Unknown Practitioner';
};

const getPractitionerEmail = (practitioner: Practitioner): string | null => {
  const email = practitioner.telecom?.find((t) => t.system === 'email');
  return email?.value || null;
};

const getPractitionerPhone = (practitioner: Practitioner): string | null => {
  const phone = practitioner.telecom?.find((t) => t.system === 'phone');
  return phone?.value || null;
};

const getPractitionerQualification = (practitioner: Practitioner): string | null => {
  return practitioner.qualification?.[0]?.code?.text || null;
};

export function PractitionersPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  // Table filters and sorting
  const { activeFilters, setFilters } = useTableFilters({
    onFiltersChange: () => setPage(1), // Reset to page 1 when filters change
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['practitioners', debouncedSearch, page, activeFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedSearch) {
        params.append('name', debouncedSearch);
      }
      // Apply active status filter
      if (activeFilters.active) {
        params.append('active', activeFilters.active as string);
      }
      params.append('_count', String(PAGE_SIZE));
      params.append('_offset', String((page - 1) * PAGE_SIZE));
      const url = `/Practitioner?${params.toString()}`;
      const response = await apiClient.get<PractitionerBundle>(url);
      return response.data;
    },
  });

  const practitioners: Practitioner[] = data?.entry?.map((e) => e.resource) || [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  // Define columns for the table with inline filters
  const columns: TableColumn<Practitioner>[] = [
    {
      key: 'name',
      header: 'Practitioner Name',
      sortable: true,
      sortKey: 'name',
      render: (practitioner) => (
        <AvatarCell
          name={getPractitionerName(practitioner)}
          subtitle={getPractitionerEmail(practitioner)}
        />
      ),
    },
    {
      key: 'specialization',
      header: 'Specialization',
      render: (practitioner) => (
        <TextCell>{getPractitionerQualification(practitioner) || '—'}</TextCell>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (practitioner) => <TextCell>{getPractitionerPhone(practitioner) || '—'}</TextCell>,
    },
    {
      key: 'status',
      header: 'Status',
      filter: {
        type: 'select',
        options: PRACTITIONER_STATUS_OPTIONS,
        value: activeFilters.active as string | undefined,
        onChange: (value) => setFilters({ ...activeFilters, active: value }),
      },
      render: () => (
        <div className="text-center">
          <StatusBadge status="active" />
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Action',
      render: () => (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              /* TODO: View practitioner */
            }}
            className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100"
            title="View"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              /* TODO: Edit practitioner */
            }}
            className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100"
            title="Edit"
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      <TableView
        title="Practitioners"
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          placeholder: 'Search Practitioner',
        }}
        addButton={{
          label: 'Add Practitioner',
          onClick: () => {
            /* TODO: Add practitioner modal */
          },
        }}
        columns={columns}
        gridTemplate={PRACTITIONERS_TABLE_GRID}
        data={practitioners}
        getRowKey={(p) => p.id}
        isLoading={isLoading}
        error={error ? 'Failed to load practitioners. Please try again.' : null}
        onRetry={() => refetch()}
        onRefresh={() => refetch()}
        emptyMessage="No practitioners found."
        emptySearchMessage="No practitioners found matching your search."
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          total,
          hasPrev,
          hasNext,
          onPrev: () => setPage((p) => Math.max(1, p - 1)),
          onNext: () => setPage((p) => Math.min(totalPages, p + 1)),
        }}
      />
    </AppLayout>
  );
}
